import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CountryCode } from 'libphonenumber-js';
import { ArrowRight, Check, Circle, MinusCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { MemberImportDialog } from '@/pages/people/member-import-dialog';
import { SAMPLE_CSV } from '@/pages/people/member-import-csv';
import { useChurchStore, type PublicChurch } from '@/stores/church-store';

type StepId = 'people' | 'hubtelSms' | 'invites' | 'staff' | 'books';
type StepStatus = 'pending' | 'done' | 'skipped';

interface MigrationInviteSummary {
  notLinked: number;
  readyEmail: number;
  readySms: number;
  readyBoth: number;
  noContact: number;
  smsBlocked: boolean;
}

interface MigrationAssistantState {
  dismissed: boolean;
  steps: Record<StepId, StepStatus>;
  booksStartDate: string | null;
  hints: {
    hubtelSmsReady: boolean;
    messagingModuleEnabled: boolean;
    memberCount: number;
    fundCount: number;
    periodCount: number;
    staffConsoleCount: number;
    inviteSummary: MigrationInviteSummary;
  };
}

type InviteChannel = 'email' | 'sms' | 'both';

const STEP_ORDER: StepId[] = ['people', 'hubtelSms', 'invites', 'staff', 'books'];

const inviteConfirmCopy = (
  channel: InviteChannel | null,
): 'confirmEmail' | 'confirmSms' | 'confirmBoth' => {
  if (channel === 'email') return 'confirmEmail';
  if (channel === 'sms') return 'confirmSms';
  return 'confirmBoth';
};

const inviteConfirmCountFor = (
  channel: InviteChannel | null,
  emailEligible: number,
  smsEligible: number,
  bothEligible: number,
): number => {
  if (channel === 'email') return emailEligible;
  if (channel === 'sms') return smsEligible;
  return bothEligible;
};

const statusIcon = (status: StepStatus) => {
  if (status === 'done') return <Check className="size-4 text-brand" aria-hidden />;
  if (status === 'skipped') return <MinusCircle className="size-4 text-fg-tertiary" aria-hidden />;
  return <Circle className="size-4 text-fg-tertiary" aria-hidden />;
};

const downloadSampleCsv = () => {
  const blob = new Blob([SAMPLE_CSV], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'trinity-member-import-sample.csv';
  anchor.click();
  URL.revokeObjectURL(url);
};

export const MigrationAssistantPage = () => {
  const { t } = useTranslation('migration');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showSkipActions = searchParams.get('onboarding') === '1';
  const queryClient = useQueryClient();
  const church = useChurchStore((state) => state.church);
  const refreshCurrent = useChurchStore((state) => state.refreshCurrent);

  const [importOpen, setImportOpen] = useState(false);
  const [inviteChannel, setInviteChannel] = useState<InviteChannel | null>(null);
  const [booksStartDate, setBooksStartDate] = useState<string | null>(null);

  const assistant = useQuery({
    queryKey: ['migration-assistant'],
    queryFn: () => api.get<MigrationAssistantState>('/migration-assistant'),
  });

  const data = assistant.data;

  useEffect(() => {
    if (!data) return;
    setBooksStartDate(data.booksStartDate);
  }, [data?.booksStartDate]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['migration-assistant'] });
  };

  const patchAssistant = useMutation({
    mutationFn: (body: {
      dismissed?: boolean;
      steps?: Partial<Record<StepId, StepStatus>>;
    }) => api.patch<MigrationAssistantState>('/migration-assistant', body),
    onSuccess: () => {
      invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const skipStep = (step: StepId) => {
    patchAssistant.mutate(
      { steps: { [step]: 'skipped' } },
      {
        onSuccess: () => toast.success(t('toast.stepSkipped')),
      },
    );
  };

  const markDone = (step: StepId) => {
    patchAssistant.mutate({ steps: { [step]: 'done' } });
  };

  const dismiss = () => {
    patchAssistant.mutate(
      { dismissed: true },
      {
        onSuccess: () => {
          toast.success(t('toast.dismissed'));
          void navigate('/home');
        },
      },
    );
  };

  const sendInvites = useMutation({
    mutationFn: (channel: InviteChannel) =>
      api.post<{ sent: number; skipped: number; failures: Array<{ memberId: string; code: string }> }>(
        '/migration-assistant/congregation-invites',
        { channel },
      ),
    onSuccess: (result) => {
      setInviteChannel(null);
      invalidate();
      toast.success(t('steps.invites.sent', { count: result.sent }));
      if (result.failures.length > 0) {
        toast.error(t('steps.invites.failures', { count: result.failures.length }));
      }
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const saveBooks = useMutation({
    mutationFn: () =>
      api.patch<PublicChurch>('/churches/current', {
        booksStartDate,
      }),
    onSuccess: async () => {
      await refreshCurrent();
      markDone('books');
      toast.success(t('steps.books.saved'));
      invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const doneCount = data
    ? STEP_ORDER.filter((step) => data.steps[step] === 'done' || data.steps[step] === 'skipped').length
    : 0;

  const defaultCountry = (church?.country ?? 'GH') as CountryCode;
  const summary = data?.hints.inviteSummary;
  const emailEligible =
    (summary?.readyEmail ?? 0) + (summary?.readyBoth ?? 0);
  const smsEligible =
    (summary?.readySms ?? 0) + (summary?.readyBoth ?? 0);
  const bothEligible =
    (summary?.readyEmail ?? 0) + (summary?.readySms ?? 0) + (summary?.readyBoth ?? 0);
  const inviteConfirmKey = inviteConfirmCopy(inviteChannel);
  const inviteConfirmCount = inviteConfirmCountFor(
    inviteChannel,
    emailEligible,
    smsEligible,
    bothEligible,
  );

  return (
    <div>
      <PageHeader
        title={t('title')}
        description={t('description')}
        headerActions={
          showSkipActions ? (
            <Button type="button" variant="outline" onClick={dismiss} disabled={patchAssistant.isPending}>
              {t('dismiss')}
            </Button>
          ) : undefined
        }
      />

      {assistant.isLoading ? (
        <p className="text-body-sm text-fg-secondary">{tCommon('state.loading')}</p>
      ) : assistant.isError ? (
        <p className="text-body-sm text-danger">{tErrors('UNEXPECTED')}</p>
      ) : data ? (
        <div className="space-y-4">
          <p className="text-caption text-fg-secondary">
            {t('progress', { done: doneCount, total: STEP_ORDER.length })}
          </p>

          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2 text-body">
                  {statusIcon(data.steps.people)}
                  {t('steps.people.title')}
                </CardTitle>
                <CardDescription>{t('steps.people.description')}</CardDescription>
              </div>
              <Badge variant={data.steps.people === 'done' ? 'success' : 'neutral'}>
                {t(`status.${data.steps.people}`)}
              </Badge>
            </CardHeader>
            <CardContent className="flex flex-wrap justify-end gap-2">
              {showSkipActions ? (
                <Button type="button" variant="outline" size="sm" onClick={() => skipStep('people')}>
                  {t('actions.skip')}
                </Button>
              ) : null}
              <Button type="button" variant="outline" size="sm" onClick={downloadSampleCsv}>
                {t('steps.people.downloadSample')}
              </Button>
              <Button type="button" variant="outline" size="sm" asChild>
                <Link to="/people">{t('steps.people.addOne')}</Link>
              </Button>
              <Button type="button" size="sm" onClick={() => setImportOpen(true)}>
                {t('steps.people.import')}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2 text-body">
                  {statusIcon(data.steps.hubtelSms)}
                  {t('steps.hubtelSms.title')}
                </CardTitle>
                <CardDescription>{t('steps.hubtelSms.description')}</CardDescription>
              </div>
              <Badge
                variant={
                  data.steps.hubtelSms === 'done'
                    ? 'success'
                    : data.hints.hubtelSmsReady
                      ? 'success'
                      : 'neutral'
                }
              >
                {data.hints.hubtelSmsReady
                  ? t('status.done')
                  : t(`status.${data.steps.hubtelSms}`)}
              </Badge>
            </CardHeader>
            <CardContent className="space-y-3">
              {showSkipActions ? (
                <p className="text-caption text-fg-secondary">{t('steps.hubtelSms.skipHint')}</p>
              ) : null}
              <div className="flex flex-wrap justify-end gap-2">
                {showSkipActions ? (
                  <Button type="button" variant="outline" size="sm" onClick={() => skipStep('hubtelSms')}>
                    {t('actions.skip')}
                  </Button>
                ) : null}
                <Button type="button" size="sm" asChild>
                  <Link to="/settings/hubtel/connection">
                    {t('steps.hubtelSms.connect')}
                    <ArrowRight className="size-3" aria-hidden />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2 text-body">
                  {statusIcon(data.steps.invites)}
                  {t('steps.invites.title')}
                </CardTitle>
                <CardDescription>{t('steps.invites.description')}</CardDescription>
              </div>
              <Badge variant={data.steps.invites === 'done' ? 'success' : 'neutral'}>
                {t(`status.${data.steps.invites}`)}
              </Badge>
            </CardHeader>
            <CardContent className="space-y-3">
              {summary ? (
                <ul className="space-y-1 text-caption text-fg-secondary">
                  <li>{t('steps.invites.summary', { notLinked: summary.notLinked })}</li>
                  {summary.readyEmail > 0 ? (
                    <li>{t('steps.invites.readyEmail', { count: summary.readyEmail })}</li>
                  ) : null}
                  {summary.readySms > 0 ? (
                    <li>{t('steps.invites.readySms', { count: summary.readySms })}</li>
                  ) : null}
                  {summary.readyBoth > 0 ? (
                    <li>{t('steps.invites.readyBoth', { count: summary.readyBoth })}</li>
                  ) : null}
                  {summary.noContact > 0 ? (
                    <li>{t('steps.invites.noContact', { count: summary.noContact })}</li>
                  ) : null}
                </ul>
              ) : null}
              <div className="flex flex-wrap justify-end gap-2">
                {showSkipActions ? (
                  <Button type="button" variant="outline" size="sm" onClick={() => skipStep('invites')}>
                    {t('actions.skip')}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={emailEligible === 0 || sendInvites.isPending}
                  loading={sendInvites.isPending && sendInvites.variables === 'email'}
                  onClick={() => setInviteChannel('email')}
                >
                  {t('steps.invites.sendEmail')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  disabled={smsEligible === 0 || sendInvites.isPending}
                  loading={sendInvites.isPending && sendInvites.variables === 'sms'}
                  onClick={() => setInviteChannel('sms')}
                >
                  {t('steps.invites.sendSms')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  disabled={
                    emailEligible + smsEligible === 0 || sendInvites.isPending
                  }
                  loading={sendInvites.isPending && sendInvites.variables === 'both'}
                  onClick={() => setInviteChannel('both')}
                >
                  {t('steps.invites.sendBoth')}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2 text-body">
                  {statusIcon(data.steps.staff)}
                  {t('steps.staff.title')}
                </CardTitle>
                <CardDescription>{t('steps.staff.description')}</CardDescription>
              </div>
              <Badge variant={data.steps.staff === 'done' ? 'success' : 'neutral'}>
                {t(`status.${data.steps.staff}`)}
              </Badge>
            </CardHeader>
            <CardContent className="flex flex-wrap justify-end gap-2">
              {showSkipActions ? (
                <Button type="button" variant="outline" size="sm" onClick={() => skipStep('staff')}>
                  {t('actions.skip')}
                </Button>
              ) : null}
              <Button type="button" size="sm" asChild>
                <Link to="/settings/staff">
                  {t('steps.staff.invite')}
                  <ArrowRight className="size-3" aria-hidden />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
              <div className="space-y-1">
                <CardTitle className="flex items-center gap-2 text-body">
                  {statusIcon(data.steps.books)}
                  {t('steps.books.title')}
                </CardTitle>
                <CardDescription>{t('steps.books.description')}</CardDescription>
              </div>
              <Badge variant={data.steps.books === 'done' ? 'success' : 'neutral'}>
                {t(`status.${data.steps.books}`)}
              </Badge>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 max-w-md">
                  <FormField label={t('steps.books.goLiveDate')} hint={t('steps.books.goLiveDateHint')}>
                    {(field) => (
                      <DatePicker
                        id={field.id}
                        className="w-56 max-w-full"
                        value={booksStartDate}
                        onChange={setBooksStartDate}
                      />
                    )}
                  </FormField>
                </div>
                <div className="flex flex-wrap gap-2 sm:justify-end sm:pt-7">
                  <Button type="button" variant="outline" size="sm" asChild>
                    <Link to="/giving/funds">{t('steps.books.funds')}</Link>
                  </Button>
                  <Button type="button" variant="outline" size="sm" asChild>
                    <Link to="/giving/periods">{t('steps.books.periods')}</Link>
                  </Button>
                  <Button type="button" variant="outline" size="sm" asChild>
                    <Link to="/giving/funds">{t('steps.books.openingBalances')}</Link>
                  </Button>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                {showSkipActions ? (
                  <Button type="button" variant="outline" size="sm" onClick={() => skipStep('books')}>
                    {t('actions.skip')}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  loading={saveBooks.isPending}
                  onClick={() => saveBooks.mutate()}
                >
                  {t('steps.books.saveBooks')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}

      <ConfirmDialog
        open={inviteChannel !== null}
        onOpenChange={(open) => {
          if (!open && !sendInvites.isPending) setInviteChannel(null);
        }}
        title={t(`steps.invites.${inviteConfirmKey}.title`)}
        description={t(`steps.invites.${inviteConfirmKey}.body`, {
          count: inviteConfirmCount,
        })}
        confirmLabel={t(`steps.invites.${inviteConfirmKey}.confirm`)}
        cancelLabel={tCommon('action.cancel')}
        loading={sendInvites.isPending}
        onConfirm={() => {
          if (inviteChannel) sendInvites.mutate(inviteChannel);
        }}
      />

      <MemberImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        defaultCountry={defaultCountry}
        afterImportNavigation="none"
        onImported={() => {
          invalidate();
        }}
      />
    </div>
  );
};
